import {
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { Subscription } from 'rxjs';

import { IAnnouncement } from '@shared/interfaces/announcement.interface';
import { AnnouncementsService } from '@shared/services/announcements.service';
import { LoggerService } from '@shared/services/logger.service';
import { ToastService } from '@shared/services/toast.service';

@Component({
  selector: 'app-announcement-sidebar',
  templateUrl: './announcement-sidebar.component.html',
  standalone: false
})
export class AnnouncementSidebarComponent implements OnInit, OnDestroy {
  @ViewChild('closeBtn', { static: false })
  public closeBtn: ElementRef<HTMLButtonElement>;

  public isOpen = false;
  public isLoading = false;
  public announcements: IAnnouncement[] = [];
  public expandedId: string | null = null;

  private readonly constructorName: string = String(this.constructor.name);

  private subscriptions: Subscription[] = [];

  constructor(
    private readonly _announcements: AnnouncementsService,
    private readonly _logger: LoggerService,
    private readonly _toast: ToastService
  ) {}

  public ngOnInit(): void {
    this.subscriptions.push(
      this._announcements.sidebarOpen$.subscribe((open) => {
        this.isOpen = open;
        if (open) {
          this.expandedId = null;
          this.load();
          setTimeout(() => this.closeBtn?.nativeElement.focus());
        }
      })
    );
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  @HostListener('document:keydown.escape')
  public onEscape(): void {
    if (this.isOpen) {
      this.close();
    }
  }

  public close(): void {
    this._announcements.closeSidebar();
  }

  public markAllRead(): void {
    const url = 'POST /announcements/read_all';
    this._announcements.markAllRead().subscribe({
      next: () => {
        this.announcements = this.announcements.map((a) => ({ ...a, is_read: true }));
      },
      error: (err) => {
        this._logger.error(this.constructorName, url, err);
        this._toast.error('Unable to update notifications. Please retry');
      }
    });
  }

  public onItemClick(announcement: IAnnouncement): void {
    this.expandedId = this.expandedId === announcement.id ? null : announcement.id;

    if (announcement.is_read) {
      return;
    }
    const url = `POST /announcements/${announcement.id}/read`;
    this._announcements.markRead(announcement.id).subscribe({
      next: () => {
        announcement.is_read = true;
      },
      error: (err) => {
        this._logger.error(this.constructorName, url, err);
        this._toast.error('Unable to update notifications. Please retry');
      }
    });
  }

  public isExpanded(announcement: IAnnouncement): boolean {
    return this.expandedId === announcement.id;
  }

  public previewOf(announcement: IAnnouncement): string {
    const text = announcement.body.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    return text.length > 120 ? `${text.slice(0, 120)}…` : text;
  }

  private load(): void {
    this.isLoading = true;
    const url = 'GET /announcements';
    this._announcements.getAnnouncements(1).subscribe({
      next: (page) => {
        this.announcements = page.items;
        this.isLoading = false;
      },
      error: (err) => {
        this.isLoading = false;
        this._logger.error(this.constructorName, url, err);
        this._toast.error('Unable to load notifications. Please retry');
      }
    });
  }
}
